import { emit } from './emit';

/**
 * Typed interaction events for RUM tracking across mobile pages.
 *
 * Every mobile page should track meaningful user interactions using this
 * utility. Call it in event handlers — never wrap Stencil components.
 *
 * @example
 * ```tsx
 * trackInteraction({ type: 'filter_used', page: 'audit-list', filterKey: 'status', valueCount: 2 });
 * trackInteraction({ type: 'card_tapped', page: 'audit-list', entityId: auditId, entityType: 'audit' });
 * ```
 */
export type InteractionEvent =
  | {
      type: 'page_viewed';
      page: string;
    }
  | {
      type: 'filter_used';
      page: string;
      filterKey: string;
      valueCount: number;
    }
  | {
      type: 'search_executed';
      page: string;
      queryLength: number;
    }
  | {
      type: 'search_focused';
      page: string;
    }
  | {
      type: 'card_tapped';
      page: string;
      entityId: string;
      entityType: string;
    }
  | {
      type: 'action_triggered';
      page: string;
      action: string;
      entityId?: string;
    }
  | {
      type: 'infinite_scroll_page_loaded';
      page: string;
      pageNumber: number;
    };

/**
 * Records a user interaction event to CloudWatch RUM + structured logger.
 * Uses the existing `emit` infrastructure — no new RUM setup needed.
 *
 * Call this in event handlers, NOT inside component wrappers:
 * ```tsx
 * const handleFilterChange = (key: string) => (value: unknown) => {
 *   setFilters(prev => ({ ...prev, [key]: value }));
 *   trackInteraction({ type: 'filter_used', page: 'audit-list', filterKey: key, valueCount: ... });
 * };
 * ```
 */
export function trackInteraction(event: InteractionEvent): void {
  const { type, ...payload } = event;
  emit(`interaction.${type}`, payload);
}
